import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import http from "@/api";
import {
  FileText, Download, Shield, LayoutDashboard, BarChart3,
  ScrollText, Mic, Package, Code2, BookOpen, FlaskConical,
} from "lucide-react";

/**
 * DEV — Panel exclusivo del owner. Acceso a:
 *  - Vistas internas: Readiness Técnico, Architecture Review, Analytics Demo
 *  - Descargas: Deck PDF/PPTX, Propuesta MD/DOCX, Speech, Guía Demo TI,
 *               Protocolo de Seguridad, Architecture Review PDF, Docs internos,
 *               Revision Pack ZIP.
 *
 * Ningún otro rol (admin/evaluator/buyer/supplier/tech_admin/stakeholder) ve esta página.
 */
export default function DevPanel() {
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => { document.title = "DEV — LALA Elite"; }, []);

  const flash = (m, isErr = false) => {
    if (isErr) { setErr(m); setTimeout(() => setErr(""), 5000); }
    else { setMsg(m); setTimeout(() => setMsg(""), 3000); }
  };

  const downloadBlob = async (key, url, filename, mime, params = {}) => {
    setBusyKey(key);
    try {
      const res = await http.get(url, { responseType: "blob", params });
      const blob = new Blob([res.data], { type: mime });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      flash(`Descargado: ${filename}`);
    } catch (e) {
      flash(e.response?.data?.detail || e.message || "No se pudo descargar.", true);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="dev-panel">
      {/* Header */}
      <header className="mb-8">
        <div className="font-hand text-sm text-[var(--lala-blue)]">interno · solo owner</div>
        <h1 className="font-display text-4xl flex items-center gap-3" data-testid="dev-title">
          <Code2 className="text-[var(--lala-blue)]" size={36} /> DEV
        </h1>
        <p className="text-[var(--ink-soft)] mt-2 max-w-3xl">
          Tu panel privado. Acá viven las vistas técnicas (Readiness, Architecture Review,
          Analytics) y las descargas comerciales (Deck, Propuesta, Speech, Guía TI, Revision Pack)
          que <strong>ningún otro rol puede ver</strong>. Si lo ven los demás, abrí un bug acá mismo.
        </p>
      </header>

      {msg && <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3" data-testid="dev-msg">{msg}</div>}
      {err && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="dev-err">{err}</div>}

      {/* HERO — Guía Maestra consolidada para Lala */}
      <section className="mb-10" data-testid="dev-hero-guia-maestra">
        <div className="rounded-2xl overflow-hidden border-2 border-[var(--lala-blue)] shadow-lg relative"
             style={{
               backgroundImage: "url('/brand/lala_elite_banner_blue.png')",
               backgroundSize: "cover",
               backgroundPosition: "center",
             }}>
          {/* Overlay for text legibility */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0033AD]/85 via-[#0033AD]/70 to-[#00267F]/85"></div>
          <div className="relative p-8 flex items-start gap-6 text-white">
            <div className="shrink-0 relative">
              {/* Floating star with drop shadow + subtle animation */}
              <img
                src="/brand/lala_elite_star_clean_sm.png"
                alt="LALA Elite"
                className="w-40 h-auto object-contain drop-shadow-2xl"
                style={{
                  filter: "drop-shadow(0 12px 20px rgba(0,0,0,0.35))",
                  animation: "floatStar 4s ease-in-out infinite",
                }}
              />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-[#FFC400] font-bold mb-2">📕 Documento consolidado · un solo archivo</div>
              <h2 className="font-display text-3xl mb-2">Guía Maestra LALA Elite</h2>
              <p className="text-white/85 text-sm leading-relaxed mb-4 max-w-2xl">
                Un solo PDF con logo oficial, guía de uso paso a paso (cómo el evaluador captura sin Excel · sin manual), proceso mes a mes, arquitectura + AWS + seguridad para IT, modelo comercial y roadmap. Consolida las 8 secciones que Lala pidió antes del viernes.
              </p>
              <button
                onClick={() => downloadBlob("guia-maestra", "/admin/guia-maestra-lala.pdf",
                  "LALA_Elite_Guia_Maestra.pdf", "application/pdf")}
                data-testid="dl-guia-maestra"
                disabled={busyKey === "guia-maestra"}
                className="sketch-pill bg-[#FFC400] text-[var(--ink)] px-5 py-3 text-sm font-bold inline-flex items-center gap-2 hover:opacity-90 disabled:opacity-50"
              >
                <FileText size={16} />
                {busyKey === "guia-maestra" ? "Generando PDF…" : "Descargar Guía Maestra (PDF)"}
                <Download size={16} />
              </button>
              <p className="text-white/60 text-xs mt-3">Este es el archivo único para enviar a Florencia, Carolina, IT y Comercial de Lala.</p>
            </div>
          </div>
        </div>
        <style>{`
          @keyframes floatStar {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-8px) rotate(1.5deg); }
          }
        `}</style>
      </section>

      {/* Sección 0 — Sesión IT martes (URGENTE) */}
      <section className="mb-10" data-testid="dev-tuesday-section">
        <h2 className="font-display text-2xl mb-3 flex items-center gap-2">
          <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
          Entregables sesión viernes — 04/07 · 13:00
        </h2>
        <p className="text-sm text-[var(--ink-soft)] mb-4">
          Documentos comprometidos por Cristian en junta 30/06 · enviar por correo antes del jueves 03/07 EOD.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <DevDownload
            icon={<LayoutDashboard size={18} />}
            title="1 · Flujo de PROCESO de negocio"
            desc="Cómo cada stakeholder alimenta KPIs mes a mes hasta el dashboard y la premiación anual. Incluye propuesta de nomenclatura Óscar/Astronomía/Naturaleza."
            highlight
            actions={[
              { label: "MD", testid: "dl-flow-proc", color: "yellow",
                onClick: () => downloadBlob("flow-proc", "/docs/flujo-proceso-negocio",
                  "Lala_Elite_Flujo_Proceso_Negocio_v1.md", "text/markdown", { format: "md" }) },
            ]}
            busyKey={busyKey}
          />
          <DevDownload
            icon={<ScrollText size={18} />}
            title="2 · Carta a Santa Claus"
            desc="Listado de lo que Encubex necesita de Lala (datos · gente · decisiones · accesos) antes del arranque productivo."
            highlight
            actions={[
              { label: "MD", testid: "dl-carta", color: "yellow",
                onClick: () => downloadBlob("carta", "/docs/carta-a-lala",
                  "Lala_Elite_Carta_a_Lala.md", "text/markdown", { format: "md" }) },
            ]}
            busyKey={busyKey}
          />
          <DevDownload
            icon={<Shield size={18} />}
            title="3 · Dudas formales de ciberseguridad"
            desc="36 preguntas en 6 bloques. Pedimos respuesta a bloques 1, 2 y 3 antes del viernes."
            actions={[
              { label: "MD", testid: "dl-security-q", color: "blue",
                onClick: () => downloadBlob("sec-q", "/docs/security-questions",
                  "Lala_Elite_Preguntas_Seguridad_IT_Lala.md", "text/markdown", { format: "md" }) },
            ]}
            busyKey={busyKey}
          />
          <DevDownload
            icon={<LayoutDashboard size={18} />}
            title="4 · Flujo transaccional (técnico)"
            desc="Diagrama de arquitectura con 8 decisiones pendientes de IT Lala."
            actions={[
              { label: "MD", testid: "dl-flow", color: "blue",
                onClick: () => downloadBlob("flow", "/docs/flujo-transaccional",
                  "Lala_Elite_Flujo_Transaccional_v0.md", "text/markdown", { format: "md" }) },
            ]}
            busyKey={busyKey}
          />
          <DevDownload
            icon={<FileText size={18} />}
            title="5 · Matching Modelo V1 ↔ plataforma"
            desc="Cruce 1-a-1 pedido por Carolina: qué del Modelo V1 oficial ya está listo (55%) · parcial (33%) · pendiente (8%). Resuelve las diferencias del Modelo V1 oficial vs. minuta 24/06."
            highlight
            actions={[
              { label: "MD", testid: "dl-matching", color: "yellow",
                onClick: () => downloadBlob("matching", "/docs/matching-modelo-v1",
                  "Lala_Elite_Matching_Modelo_V1_vs_Plataforma.md", "text/markdown", { format: "md" }) },
            ]}
            busyKey={busyKey}
          />
          <DevDownload
            icon={<FileText size={18} />}
            title="6 · Proceso Operativo v2 (Feb 2026)"
            desc="Cómo se carga, se mueve y se publica la información mes a mes. Actores, ciclo mensual, endpoints, storage, Fase 2, SLA. Documento para dárselo al IT de Lala."
            highlight
            actions={[
              { label: "MD", testid: "dl-proceso", color: "blue",
                onClick: () => downloadBlob("proceso", "/docs/proceso-operativo",
                  "Lala_Elite_Proceso_Operativo_v2.md", "text/markdown", { format: "md" }) },
            ]}
            busyKey={busyKey}
          />
          <DevDownload
            icon={<FileText size={18} />}
            title="7 · Paquete IT Handoff (PDF único, público)"
            desc="34 páginas · 1 MB · consolida Architecture Review + Security Protocol + Flujo Transaccional + Preguntas de Seguridad. Enlace público — se comparte con IT de Lala sin login."
            highlight
            actions={[
              { label: "PDF", testid: "dl-it-handoff", color: "blue",
                onClick: () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/public/it-handoff`, "_blank") },
              { label: "Copiar link", testid: "copy-it-handoff-link", color: "yellow",
                onClick: async () => {
                  const url = `${window.location.origin}/api/public/it-handoff`;
                  await navigator.clipboard.writeText(url);
                } },
            ]}
            busyKey={busyKey}
          />
        </div>
      </section>

      {/* Sección 1 — Vistas internas */}
      <section className="mb-10" data-testid="dev-views-section">
        <h2 className="font-display text-2xl mb-3">Vistas internas</h2>
        <p className="text-sm text-[var(--ink-soft)] mb-4">
          Páginas que antes estaban en el nav de admin/tech_admin. Movidas acá para que el
          equipo Lala no las vea hasta que vos decidas exponerlas.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <DevLink
            to="/readiness"
            icon={<Shield size={18} />}
            title="Readiness Técnico"
            desc="Catálogo preliminar ~89% + descarga PDF ejecutivo."
            testid="dev-link-readiness"
          />
          <DevLink
            to="/architecture-review"
            icon={<LayoutDashboard size={18} />}
            title="Architecture Review"
            desc="81 controles enterprise con filtros + export PDF/XLSX/CSV."
            testid="dev-link-architecture"
          />
          <DevLink
            to="/admin/demo-analytics"
            icon={<BarChart3 size={18} />}
            title="Analytics Demo"
            desc="Cómo se comporta el visitante del demo público (sin PII)."
            testid="dev-link-analytics"
          />
        </div>
      </section>

      {/* Sección 2 — Material comercial */}
      <section className="mb-10" data-testid="dev-comercial-section">
        <h2 className="font-display text-2xl mb-3">Material comercial</h2>
        <p className="text-sm text-[var(--ink-soft)] mb-4">
          Para junta con Lala, IT, comercial o terceros. Versiones editables para que tu
          diseñador retoque sin tocar la plataforma.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <DevDownload
            icon={<FileText size={18} />}
            title="Pitch Deck"
            desc="PDF con paleta Lala + radar real + screenshots. 1.6 MB."
            actions={[
              { label: "PDF", testid: "dl-deck-pdf", color: "blue",
                onClick: () => downloadBlob("deck-pdf", "/docs/presentation-deck",
                  "LALA_Elite_Deck.pdf", "application/pdf", { format: "pdf" }) },
              { label: "PPTX editable", testid: "dl-deck-pptx", color: "ghost",
                onClick: () => downloadBlob("deck-pptx", "/docs/presentation-deck-pptx",
                  "LALA_Elite_Deck.pptx", "application/vnd.openxmlformats-officedocument.presentationml.presentation") },
            ]}
            busyKey={busyKey}
          />
          <DevDownload
            icon={<ScrollText size={18} />}
            title="Propuesta comercial"
            desc="Pricing por módulos + roadmap Fase 1/2/3 + escenarios."
            actions={[
              { label: "MD", testid: "dl-propuesta-md", color: "blue",
                onClick: () => downloadBlob("prop-md", "/docs/propuesta-comercial",
                  "LALA_Elite_Propuesta.md", "text/markdown", { format: "md" }) },
              { label: "DOCX editable", testid: "dl-propuesta-docx", color: "ghost",
                onClick: () => downloadBlob("prop-docx", "/docs/propuesta-comercial-docx",
                  "LALA_Elite_Propuesta.docx", "application/vnd.openxmlformats-officedocument.wordprocessingml.document") },
            ]}
            busyKey={busyKey}
          />
          <DevDownload
            icon={<Mic size={18} />}
            title="Speech junta Comercial + IT"
            desc="Guion 7 actos con texto literal + acciones en pantalla + FAQ técnico."
            highlight
            actions={[
              { label: "Speech PDF", testid: "dl-speech", color: "yellow",
                onClick: () => downloadBlob("speech", "/admin/speech-comercial-it.pdf",
                  "LALA_Elite_Speech_Comercial_IT.pdf", "application/pdf") },
            ]}
            busyKey={busyKey}
          />
          <DevDownload
            icon={<BookOpen size={18} />}
            title="Guía Demo TI"
            desc="Checklist + estructura demo + talking points + cuentas."
            actions={[
              { label: "PDF", testid: "dl-demo-ti", color: "blue",
                onClick: () => downloadBlob("demo-ti", "/admin/demo-ti-guide.pdf",
                  "LALA_Elite_Demo_TI_Guide.pdf", "application/pdf") },
            ]}
            busyKey={busyKey}
          />
        </div>
      </section>

      {/* Sección 3 — Docs internos */}
      <section className="mb-10" data-testid="dev-internal-docs-section">
        <h2 className="font-display text-2xl mb-3">Documentos internos</h2>
        <p className="text-sm text-[var(--ink-soft)] mb-4">
          Tres PDFs que normalmente compartías con Lala vía el admin. Quedan acá.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <DevDownload
            icon={<FileText size={18} />}
            title="Documento de Entendimiento"
            desc="Alcance del programa Lala Elite."
            actions={[
              { label: "PDF", testid: "dl-entendimiento-pdf", color: "blue",
                onClick: () => downloadBlob("ent-pdf", "/docs/entendimiento-lala",
                  "LALA_Elite_Entendimiento.pdf", "application/pdf", { format: "pdf" }) },
              { label: "MD", testid: "dl-entendimiento-md", color: "ghost",
                onClick: () => downloadBlob("ent-md", "/docs/entendimiento-lala",
                  "LALA_Elite_Entendimiento.md", "text/markdown", { format: "md" }) },
            ]}
            busyKey={busyKey}
          />
          <DevDownload
            icon={<FileText size={18} />}
            title="Integraciones"
            desc="Estado y roadmap de integraciones futuras (Fase 2)."
            actions={[
              { label: "PDF", testid: "dl-integ-pdf", color: "blue",
                onClick: () => downloadBlob("int-pdf", "/docs/integrations",
                  "LALA_Elite_Integraciones.pdf", "application/pdf", { format: "pdf" }) },
              { label: "MD", testid: "dl-integ-md", color: "ghost",
                onClick: () => downloadBlob("int-md", "/docs/integrations",
                  "LALA_Elite_Integraciones.md", "text/markdown", { format: "md" }) },
            ]}
            busyKey={busyKey}
          />
          <DevDownload
            icon={<FileText size={18} />}
            title="Fase 5 · Certificador"
            desc="Reemplazo de la auditoría externa por Elite."
            actions={[
              { label: "PDF", testid: "dl-fase5-pdf", color: "blue",
                onClick: () => downloadBlob("f5-pdf", "/docs/fase-5-certificador",
                  "LALA_Elite_Fase5.pdf", "application/pdf", { format: "pdf" }) },
              { label: "MD", testid: "dl-fase5-md", color: "ghost",
                onClick: () => downloadBlob("f5-md", "/docs/fase-5-certificador",
                  "LALA_Elite_Fase5.md", "text/markdown", { format: "md" }) },
            ]}
            busyKey={busyKey}
          />
          <DevDownload
            icon={<Shield size={18} />}
            title="Protocolo de Seguridad"
            desc="12 páginas para IT/Seguridad Lala (LFPDPPP, BCP, subprocesadores)."
            actions={[
              { label: "PDF", testid: "dl-security", color: "blue",
                onClick: () => downloadBlob("sec", "/docs/security-protocol",
                  "LALA_Elite_Seguridad.pdf", "application/pdf") },
            ]}
            busyKey={busyKey}
          />
          <DevDownload
            icon={<LayoutDashboard size={18} />}
            title="Architecture Review (PDF)"
            desc="Documento técnico con stack, despliegue, integraciones."
            actions={[
              { label: "PDF", testid: "dl-arc-pdf", color: "blue",
                onClick: () => downloadBlob("arc", "/docs/architecture-review",
                  "LALA_Elite_Architecture.pdf", "application/pdf") },
            ]}
            busyKey={busyKey}
          />
        </div>
      </section>

      {/* Sección 4 — Revisión externa */}
      <section className="mb-10" data-testid="dev-review-section">
        <h2 className="font-display text-2xl mb-3">Revisión externa</h2>
        <div className="bg-[var(--cream-soft)] border border-[var(--line)] rounded-xl p-5 flex items-start gap-4">
          <span className="text-3xl shrink-0">📦</span>
          <div className="flex-1">
            <h3 className="font-display text-lg mb-1">Paquete de revisión externa</h3>
            <p className="text-sm text-[var(--ink-soft)] leading-snug mb-3">
              ZIP curado para revisión técnica por terceros (Claude / ChatGPT / TI Lala).
              Incluye <b>REVISION_CHECKLIST.md</b>, README, PRD, server.py, integraciones,
              páginas React y último test report. Excluye <code>.git</code>, <code>node_modules</code>,
              binarios pesados.
            </p>
            <button
              onClick={() => downloadBlob("zip", "/admin/revision-pack.zip",
                `lala-elite-revision-${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.zip`,
                "application/zip")}
              data-testid="dl-revision-pack"
              className="sketch-pill bg-[var(--ink)] text-white px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 hover:opacity-90"
              disabled={busyKey === "zip"}
            >
              <Package size={14} />
              {busyKey === "zip" ? "Generando ZIP…" : "Descargar ZIP de revisión"}
              <Download size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* Sección 5 — Notas de seguridad */}
      <section className="mb-6" data-testid="dev-notes-section">
        <h2 className="font-display text-2xl mb-3">Notas</h2>
        <div className="bg-[#FFFBEA] border border-[#FFC400] rounded-xl p-5">
          <ul className="space-y-1.5 text-sm text-[var(--ink)]">
            <li className="flex items-start gap-2"><FlaskConical size={16} className="mt-0.5 text-[var(--lala-blue)] shrink-0" />Esta página y todos sus endpoints (<code>/api/readiness*</code>, <code>/api/architecture-review*</code>, <code>/api/docs/*</code>, <code>/api/admin/demo-analytics</code>, <code>/api/admin/demo-ti-guide.pdf</code>, <code>/api/admin/speech-comercial-it.pdf</code>, <code>/api/admin/revision-pack.zip</code>) requieren rol <code>owner</code>. Cualquier otro rol recibe 403.</li>
            <li className="flex items-start gap-2"><FlaskConical size={16} className="mt-0.5 text-[var(--lala-blue)] shrink-0" />Para crear más cuentas owner, registrate via <code>POST /api/auth/register</code> con <code>role: &quot;owner&quot;</code> desde tu cuenta owner actual.</li>
            <li className="flex items-start gap-2"><FlaskConical size={16} className="mt-0.5 text-[var(--lala-blue)] shrink-0" />Cuenta seed: <code>owner@elite.dev / OwnerElite2026!</code> — cambiá la contraseña antes de compartirla.</li>
          </ul>
        </div>
      </section>
    </div>
  );
}

function DevLink({ to, icon, title, desc, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="block bg-white border border-[var(--line)] rounded-xl p-4 hover:border-[var(--lala-blue)] hover:shadow-md transition-all"
    >
      <div className="flex items-center gap-2 mb-1 text-[var(--lala-blue)]">
        {icon}
        <h3 className="font-display text-base">{title}</h3>
      </div>
      <p className="text-xs text-[var(--ink-soft)] leading-snug">{desc}</p>
    </Link>
  );
}

function DevDownload({ icon, title, desc, actions, busyKey, highlight = false }) {
  return (
    <div className={`rounded-xl p-4 border ${highlight ? "bg-[#FFFBEA] border-[#FFC400]" : "bg-white border-[var(--line)]"}`}>
      <div className="flex items-center gap-2 mb-1 text-[var(--lala-blue)]">
        {icon}
        <h3 className="font-display text-base">{title}</h3>
      </div>
      <p className="text-xs text-[var(--ink-soft)] leading-snug mb-3">{desc}</p>
      <div className="flex flex-wrap gap-2">
        {actions.map((a) => {
          const isBusy = busyKey === a.testid?.replace("dl-", "") || busyKey === a.testid;
          const cls = a.color === "yellow"
            ? "bg-[#FFC400] text-[var(--ink)] font-bold"
            : a.color === "ghost"
              ? "bg-white border border-[var(--lala-blue)] text-[var(--lala-blue)]"
              : "bg-[var(--lala-blue)] text-white";
          return (
            <button
              key={a.testid}
              onClick={a.onClick}
              data-testid={a.testid}
              disabled={isBusy}
              className={`sketch-pill px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 hover:opacity-90 disabled:opacity-50 ${cls}`}
            >
              <FileText size={12} /> {a.label} <Download size={12} />
            </button>
          );
        })}
      </div>
    </div>
  );
}
